import { Avatar, Box, Flex, HStack, Icon, Image, Separator, Stack, Text, VStack } from '@chakra-ui/react';
import { Link, useLocation, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiLibrary, BiTask } from 'react-icons/bi';
import { FaUserEdit, FaUsers } from 'react-icons/fa';
import { FiBookOpen } from 'react-icons/fi';
import { GoGoal } from 'react-icons/go';
import { MdSpaceDashboard } from 'react-icons/md';
import { TbSubtask } from 'react-icons/tb';
import { useAuth } from 'react-oidc-context';

import { R3PortalIcon } from '@/components/common/Icons';
import { Tooltip } from '@/components/ui/tooltip';
import { UserRoleEnum, isRoleGreaterOrEqual, useUserRole } from '@/providers/user-role-provider';

import Logo from '/assets/images/pilotrse-logo.svg';

interface SidebarItemProps {
  name: string;
  icon: React.ElementType;
  path?: string;
  collapsed?: boolean;
  hidden?: boolean;
  onClick?: () => void;
}

const SidebarItem = ({ name, icon, path = '', collapsed = false }: SidebarItemProps) => {
  const location = useLocation();

  const isActiveLink = (to: string) => location.pathname.startsWith(to);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Tooltip
      showArrow
      content={name}
      disabled={!collapsed}
      positioning={{ placement: 'right', gutter: 8 }}
      contentProps={{ css: { '--tooltip-bg': 'colors.primary' } }}
      openDelay={100}
      closeDelay={100}
    >
      <Link to={path} style={{ width: '100%' }}>
        <Flex
          align="center"
          py={1}
          px={2}
          borderRadius="sm"
          cursor="pointer"
          _hover={{ bg: 'primary', color: 'gray.50' }}
          width="100%"
          mb={1}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          backgroundColor={isActiveLink(path) ? 'primary' : ''}
          color={isActiveLink(path) ? 'gray.50' : ''}
        >
          <Icon
            as={icon}
            ml={collapsed ? '2px' : 0}
            mr={collapsed ? 0 : 2}
            boxSize={5}
            style={{ filter: isHovered ? 'brightness(0) invert(1)' : '' }}
          />
          <Text
            flex="1"
            maxWidth={collapsed ? 0 : '100%'}
            opacity={collapsed ? 0 : 1}
            overflow="hidden"
            whiteSpace="nowrap"
            transition="max-width 0.3s ease-in-out, opacity 0.3s ease-in-out"
          >
            {name}
          </Text>
        </Flex>
      </Link>
    </Tooltip>
  );
};

const SidebarItems = ({ collapsed = false }) => {
  const auth = useAuth();
  const { t } = useTranslation();
  const { userRole } = useUserRole();
  const { slug } = useParams({ strict: false });

  const topSidebarItems: SidebarItemProps[] = [
    {
      name: t('sidebar.dashboard'),
      icon: MdSpaceDashboard,
      path: `/${slug}/dashboard`,
    },
    { name: t('sidebar.csrPlan'), icon: TbSubtask, path: `/${slug}/csr-plan` },
    { name: t('sidebar.actions'), icon: BiTask, path: `/${slug}/actions` },
    {
      name: t('sidebar.indicators'),
      icon: GoGoal,
      path: `/${slug}/indicators`,
    },
    { name: t('sidebar.library'), icon: BiLibrary, path: `/${slug}/library` },
  ];

  const bottomSidebarItems: SidebarItemProps[] = [
    { name: t('sidebar.profile'), icon: FaUserEdit, path: `/${slug}/profile` },
    {
      name: t('sidebar.accessManagement'),
      icon: FaUsers,
      path: `/${slug}/access-management`,
      hidden: !isRoleGreaterOrEqual(userRole, UserRoleEnum.ADMIN),
    },
    {
      name: t('sidebar.changeCompany'),
      icon: FiBookOpen,
      path: '/companies',
    },
  ];

  const r3ConnectSidebarItems: SidebarItemProps[] = [
    {
      name: "R3'Connect",
      icon: R3PortalIcon,
      path: import.meta.env.VITE_R3_CONNECT_URL,
    },
  ];

  return (
    <Flex flexDirection="column" h="100%">
      <Box alignSelf={'center'} mb={4}>
        <Link to="/">
          <Image
            src={Logo}
            alt="Company Logo"
            height={collapsed ? '38px' : '70px'}
            objectFit="cover"
            objectPosition={collapsed ? '-6px' : 'center'}
            transition="all 0.3s ease-in-out"
          />
        </Link>
      </Box>
      <Separator mb={2} />

      <VStack align="start" gap={0} w="100%" flex="1">
        {topSidebarItems
          .filter((item) => !item.hidden)
          .map((item) => (
            <SidebarItem key={item.name} {...item} collapsed={collapsed} />
          ))}
      </VStack>

      <Separator mb={2} />

      <VStack align="end" gap={0} w="100%" mt="auto" mb={2}>
        {bottomSidebarItems
          .filter((item) => !item.hidden)
          .map((item) => (
            <SidebarItem key={item.name} {...item} collapsed={collapsed} />
          ))}
      </VStack>

      <Separator mb={2} />

      <VStack align="end" gap={0} w="100%" mt="auto" mb={2}>
        {r3ConnectSidebarItems
          .filter((item) => !item.hidden)
          .map((item) => (
            <SidebarItem key={item.name} {...item} collapsed={collapsed} />
          ))}
      </VStack>

      <Separator mb={2} />

      {auth.user?.profile?.email && (
        <Tooltip
          showArrow
          content={
            <>
              {auth.user?.profile?.name}
              <br />
              {auth.user?.profile?.email}
            </>
          }
          positioning={{ placement: 'right', gutter: 8 }}
          contentProps={{ css: { '--tooltip-bg': 'colors.primary' } }}
          openDelay={100}
          closeDelay={100}
        >
          <HStack key={auth.user?.profile?.email} gap="4">
            <Avatar.Root size="sm">
              <Avatar.Fallback name={auth.user?.profile?.name} />
            </Avatar.Root>
            <Stack gap="0">
              <Text
                truncate
                fontWeight="medium"
                maxWidth={collapsed ? 0 : 'calc(100% - var(--r3-spacing-6))'}
                opacity={collapsed ? 0 : 1}
                transition="max-width 0.3s ease-in-out, opacity 0.3s ease-in-out"
              >
                {auth.user?.profile?.name}
              </Text>
              <Text
                truncate
                color="fg.muted"
                textStyle="sm"
                maxWidth={collapsed ? 0 : 'calc(100% - var(--r3-spacing-6))'}
                opacity={collapsed ? 0 : 1}
                transition="max-width 0.3s ease-in-out, opacity 0.3s ease-in-out"
              >
                {auth.user?.profile?.email}
              </Text>
            </Stack>
          </HStack>
        </Tooltip>
      )}
    </Flex>
  );
};

export default SidebarItems;
